/**
 * Native Authentication Service
 *
 * Provides platform-aware authentication using:
 * - Capacitor Google Auth on iOS/Android (native sign-in)
 * - Firebase Auth on Web (popup sign-in)
 */

import { Capacitor } from '@capacitor/core';
import { GoogleAuth } from '@codetrix-studio/capacitor-google-auth';
import {
  getAuth,
  GoogleAuthProvider,
  signInWithCredential,
  signInWithPopup,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  type User,
  type Auth,
} from 'firebase/auth';
import { getApp, initializeApp, type FirebaseApp } from 'firebase/app';

// Firebase configuration from environment variables
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

// Validate Firebase configuration
const isFirebaseConfigValid = firebaseConfig.apiKey && 
  firebaseConfig.authDomain && 
  firebaseConfig.projectId;

console.log('Firebase Configuration Check:', {
  hasApiKey: !!firebaseConfig.apiKey,
  hasAuthDomain: !!firebaseConfig.authDomain,
  hasProjectId: !!firebaseConfig.projectId,
  isValid: isFirebaseConfigValid,
  authDomain: firebaseConfig.authDomain,
  projectId: firebaseConfig.projectId,
});

if (!isFirebaseConfigValid) {
  console.error('❌ Firebase configuration is incomplete. Authentication will not be available.');
  console.error('Missing env vars:', {
    apiKey: !firebaseConfig.apiKey,
    authDomain: !firebaseConfig.authDomain,
    projectId: !firebaseConfig.projectId,
  });
  console.error('This will cause the app to work in demo mode only.');
}

// Initialize Firebase app if not already initialized
let app: FirebaseApp | null = null;
let auth: Auth | null = null;

if (isFirebaseConfigValid) {
  try {
    app = getApp();
  } catch {
    try {
      app = initializeApp(firebaseConfig);
    } catch (error) {
      console.error('Failed to initialize Firebase:', error);
      console.warn('Authentication will not be available. Demo mode can still be used.');
      app = null;
    }
  }
  
  if (app) {
    try {
      auth = getAuth(app);
    } catch (error) {
      console.error('Failed to initialize Firebase Auth:', error);
      auth = null;
    }
  }
}

// Initialize Google Auth for native platforms
if (Capacitor.isNativePlatform() && isFirebaseConfigValid) {
  GoogleAuth.initialize({
    clientId: '', // Will be read from GoogleService-Info.plist on iOS
    scopes: ['profile', 'email'],
    grantOfflineAccess: true,
  });
}

/**
 * Sign in with Google - platform aware
 * Uses native auth on iOS/Android, popup on web
 */
export async function signInWithGoogle(): Promise<User> {
  if (!auth) {
    throw new Error('Firebase authentication is not available. Please check your Firebase configuration.');
  }

  if (Capacitor.isNativePlatform()) {
    // Native authentication flow
    try {
      // Use native Google Sign-In
      const result = await GoogleAuth.signIn();

      // Get ID token
      const idToken = result.authentication.idToken;

      // Create Firebase credential
      const credential = GoogleAuthProvider.credential(idToken);

      // Sign in to Firebase with the credential
      const userCredential = await signInWithCredential(auth, credential);

      return userCredential.user;
    } catch (error) {
      console.error('Native Google Sign-In failed:', error);
      throw new Error(`Native sign-in failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  } else {
    // Web authentication flow
    const googleProvider = new GoogleAuthProvider();
    googleProvider.setCustomParameters({
      prompt: 'select_account',
    });

    const result = await signInWithPopup(auth, googleProvider);
    return result.user;
  }
}

/**
 * Sign out the current user
 * Handles both native and web sign-out
 */
export async function signOut(): Promise<void> {
  if (!auth) {
    console.warn('Firebase authentication is not available');
    return;
  }

  if (Capacitor.isNativePlatform()) {
    // Sign out from native Google Auth
    try {
      await GoogleAuth.signOut();
    } catch (error) {
      console.warn('Native Google Sign-Out failed:', error);
      // Continue with Firebase sign-out even if native fails
    }
  }

  // Sign out from Firebase
  await firebaseSignOut(auth);
}

/**
 * Get the current user's ID token
 */
export async function getIdToken(): Promise<string | null> {
  if (!auth) return null;
  const user = auth.currentUser;
  if (!user) return null;
  return user.getIdToken();
}

/**
 * Subscribe to auth state changes
 */
export function onAuthChange(callback: (user: User | null) => void): () => void {
  if (!auth) {
    // Return a no-op unsubscribe function if auth is not available
    console.warn('Firebase authentication is not available');
    // IMPORTANT: Still call the callback with null to prevent app from hanging
    // This allows the app to render in demo mode even if Firebase fails
    setTimeout(() => callback(null), 0);
    return () => {};
  }
  return onAuthStateChanged(auth, callback);
}

/**
 * Refresh authentication (useful after app resume on mobile)
 */
export async function refreshAuth(): Promise<void> {
  if (!auth) return;
  if (Capacitor.isNativePlatform()) {
    const user = auth.currentUser;
    if (user) {
      // Force token refresh
      await user.getIdToken(true);
    }
  }
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
  if (!auth) return false;
  return !!auth.currentUser;
}

/**
 * Get current platform info
 */
export function getPlatformInfo() {
  return {
    platform: Capacitor.getPlatform(),
    isNative: Capacitor.isNativePlatform(),
    isWeb: !Capacitor.isNativePlatform(),
    isAndroid: Capacitor.getPlatform() === 'android',
    isIOS: Capacitor.getPlatform() === 'ios',
  };
}

export { auth };
export type { User };

